import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Edit, Eye, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { toast } from "sonner";

interface ProductCardProps {
  product: {
    id: string;
    title: string;
    description: string;
    price: number;
    category: string;
    main_image_url: string | null;
    gallery_image_urls: string[];
    active: boolean;
    sizes: {
      id: string;
      size_name: string;
      price: number;
      main_image_url?: string | null;
      gallery_image_urls: string[];
      use_main_product_image?: boolean;
    }[];
  };
  view: "grid" | "list";
  onDelete?: (id: string) => Promise<void>;
  /** Override the default edit URL (e.g. for admin editing) */
  editUrl?: string;
  /** Override the default view URL */
  viewUrl?: string;
}

const PLACEHOLDER_IMAGE = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Crect x='3' y='3' width='18' height='18' rx='2' ry='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpolyline points='21 15 16 10 5 21'/%3E%3C/svg%3E";

export function ProductCard({ product, view, onDelete, editUrl, viewUrl }: ProductCardProps) {
  const { id, title, description, price, category, main_image_url, active, sizes } = product;
  const displayPrice = sizes[0]?.price || price;
  const displayImage = main_image_url || PLACEHOLDER_IMAGE;
  const navigate = useNavigate();
  
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!onDelete) return;
    
    try {
      setIsDeleting(true);
      await onDelete(id);
      toast.success("Product deleted successfully");
    } catch (error) {
      console.error("Error deleting product:", error);
      toast.error("Failed to delete product");
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const ActionButtons = () => (
    <div className="flex gap-2">
      <Button 
        size="sm" 
        variant="ghost"
        data-product-id={id}
        data-action="view"
        onClick={() => navigate(viewUrl || `/product/${id}`)}
      >
        <Eye className="h-4 w-4" />
      </Button>
      <Button 
        size="sm" 
        variant="ghost" 
        data-product-id={id}
        data-action="edit"
        onClick={() => navigate(editUrl || `/products/edit/${id}`)}
      >
        <Edit className="h-4 w-4" />
      </Button>
      {onDelete && (
        <Button
          size="sm"
          variant="ghost"
          data-product-id={id}
          data-action="delete"
          onClick={() => setShowDeleteDialog(true)}
          className="text-destructive hover:text-destructive"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );

  if (view === "list") {
    return (
      <>
        <Card>
          <div className="p-4 flex items-center gap-4">
            <div className="h-20 w-20 flex-shrink-0">
              <img
                src={displayImage}
                alt={title}
                className="h-full w-full object-cover rounded-lg"
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-brand-secondary truncate">{title}</h3>
                <Badge variant={active ? "default" : "secondary"}>
                  {active ? "Active" : "Inactive"}
                </Badge>
              </div>
              <p className="mt-1 text-sm text-brand-secondary/70 truncate">{category}</p>
              <div className="mt-2 flex items-center justify-between">
                <p className="font-medium text-brand-secondary">
                  ${displayPrice.toFixed(2)}
                </p>
                <ActionButtons />
              </div>
            </div>
          </div>
        </Card>

        <ConfirmDialog
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          title="Delete Product"
          description="Are you sure you want to delete this product? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          onConfirm={handleDelete}
          loading={isDeleting}
        />
      </>
    );
  }

  return (
    <>
      <Card className="bg-white">
        <div className="aspect-square relative">
          <img
            src={displayImage}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {!active && (
            <div className="absolute top-2 right-2">
              <Badge variant="secondary">Inactive</Badge>
            </div>
          )}
        </div>
        <CardHeader>
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-brand-secondary truncate">{title}</h3>
            <Badge variant={active ? "default" : "secondary"}>
              {active ? "Active" : "Inactive"}
            </Badge>
          </div>
          <p className="text-sm text-brand-secondary/70 truncate">{category}</p>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-brand-secondary/70 line-clamp-2">{description}</p>
        </CardContent>
        <CardFooter className="flex items-center justify-between">
          <p className="font-medium text-brand-secondary">${displayPrice.toFixed(2)}</p>
          <ActionButtons />
        </CardFooter>
      </Card>

      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        title="Delete Product"
        description="Are you sure you want to delete this product? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        loading={isDeleting}
      />
    </>
  );
} 